import {useRef, useState, useEffect} from 'react';
import Text from '../../common/component/Text';
import {PASSIVE_TOUCH_IF_POSSIBLE} from '../../common/constant';
import {getIsTouchEvent} from '../common';

interface IProps {
  className?: string;
  value: number;
  maxValue: number;
  isRtl?: boolean;
  onChange: (value: number) => void;
}

export default function ProgressBar({
  value,
  maxValue,
  isRtl = false,
  onChange,
}: IProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const [isEnable, setIsEnable] = useState(false);

  const handleRef = useRef({
    isClicked: false,
    page: 0,
  });
  useEffect(() => {
    const barEl = barRef.current;
    if (barEl) {
      const enableBar = function () {
        setIsEnable(true);
      };

      const disableBar = function () {
        setIsEnable(false);
        disableClick();
      };

      const changePage = function (e: MouseEvent | TouchEvent) {
        const el = barEl as HTMLDivElement;
        const barWidth = el.clientWidth;
        const barX = isRtl
          ? el.getBoundingClientRect().right
          : el.getBoundingClientRect().left;
        const point = getIsTouchEvent(e) ? e.touches[0] : e;
        const xPosition = isRtl ? barX - point.clientX : point.clientX - barX;
        const page = Math.ceil((maxValue * xPosition) / barWidth);
        if (handleRef.current.page !== page) {
          handleRef.current.page = page;
          onChange(page);
        }
      };

      const onDown = function (e: MouseEvent | TouchEvent) {
        e.stopPropagation();
        e.preventDefault();
        enableBar();
        handleRef.current.isClicked = true;
        changePage(e);
      };

      const moveBar = function (e: MouseEvent | TouchEvent) {
        if (handleRef.current.isClicked) {
          changePage(e);
        }
      };

      const disableClick = function () {
        handleRef.current.isClicked = false;
      };

      barEl.addEventListener('mouseenter', enableBar);
      barEl.addEventListener('mouseleave', disableBar);
      barEl.addEventListener('mousedown', onDown);
      barEl.addEventListener('mousemove', moveBar, PASSIVE_TOUCH_IF_POSSIBLE);
      barEl.addEventListener('mouseup', disableClick);
      barEl.addEventListener('touchstart', onDown);
      barEl.addEventListener('touchmove', moveBar, PASSIVE_TOUCH_IF_POSSIBLE);
      barEl.addEventListener('touchend', disableBar);
      return () => {
        barEl.removeEventListener('mouseenter', enableBar);
        barEl.removeEventListener('mouseleave', disableBar);
        barEl.removeEventListener('mousedown', changePage);
        barEl.removeEventListener(
          'mousemove',
          moveBar,
          PASSIVE_TOUCH_IF_POSSIBLE
        );
        barEl.removeEventListener('mouseup', disableClick);
        barEl.removeEventListener('touchstart', changePage);
        barEl.removeEventListener(
          'touchmove',
          moveBar,
          PASSIVE_TOUCH_IF_POSSIBLE
        );
        barEl.removeEventListener('touchend', disableBar);
      };
    }
  }, [isRtl, maxValue, onChange]);

  const xPosition = Math.min(100, (value / maxValue) * 100);

  return (
    <div className="relative bg-transparent py-10" ref={barRef}>
      <div
        className={cn(
          'w-full cursor-pointer overflow-hidden transform transform-gpu rounded-4 h-8',
          {
            '!rounded-12 !h-24': isEnable,
          }
        )}
        style={{backfaceVisibility: 'hidden'}}
      >
        <div
          className={cn('h-full bg-black/20 pr-10', {
            'transform rotate-180': isRtl,
          })}
        >
          <div
            className={cn(
              'bg-dark-grey-04 cursor-pointer rounded-4 h-8 min-w-8',
              {
                '!min-w-24 !h-24': isEnable,
              }
            )}
            style={{width: `${xPosition}%`}}
          />
        </div>
      </div>
      <div className="absolute inset-0 w-[calc(100%-10px)]">
        <div
          className={cn(
            'absolute -translate-x-1/2 border-4 border-solid border-dark-grey-03 rounded-50per bg-white pointer-events-none',
            {
              'w-24 h-24 top-10': isEnable,
              'w-20 h-20 top-4': !isEnable,
              'ml-10': isRtl,
            }
          )}
          style={{left: `${isRtl ? 100 - xPosition : xPosition}%`}}
        >
          <div
            className={cn(
              'flex absolute -top-48 left-1/2 -translate-x-1/2 h-26 bg-dark-grey-03 px-11 items-center rounded-14',
              {
                hidden: !isEnable,
              }
            )}
          >
            <Text className="s12-bold-white !whitespace-nowrap">
              {value}
              <span className="opacity-30">/{maxValue}</span>
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}
